/**
 * ALTIORA · LMS (Formation continue) — Sprint 4
 *
 * Course catalog + detail view + inline quiz. LLQP / AMF / SOFT.
 */
import { useEffect, useState, useCallback } from "react";
import { GraduationCap, CheckCircle2, PlayCircle, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  PageShell, PageHeader, Card, Section, PrimaryButton, GhostButton,
  Loader, Badge, EmptyState, Alert,
} from "@/components/altiora/PremiumUI";


export default function Lms() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeKey, setActiveKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/lms/courses");
      setCourses(data);
    } catch { toast.error("Chargement impossible"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (activeKey) {
    return <CourseDetail courseKey={activeKey}
                          onBack={() => { setActiveKey(null); load(); }} />;
  }

  return (
    <PageShell testid="lms-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <PageHeader
          eyebrow="Sprint 4 · Formation continue"
          title="LMS · LLQP + AMF"
          subtitle="Consolidez vos acquis avec des micro-modules certifiants. Chaque cours vous accorde des unités de FC et des XP."
          testid="lms-header"
        />
        {loading && <div className="py-16 flex justify-center"><Loader /></div>}
        {!loading && courses.length === 0 && (
          <EmptyState icon={GraduationCap} title="Aucun cours disponible"
                      testid="lms-empty" />
        )}
        {!loading && courses.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map(c => (
              <Card key={c.key} className="p-4 flex flex-col justify-between"
                    testid={`lms-course-card-${c.key}`}>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant={c.category === "LLQP" ? "navy"
                                : c.category === "AMF"  ? "gold" : "green"}>
                      {c.category}
                    </Badge>
                    {c.completed && (
                      <Badge variant="green" icon={CheckCircle2}>Terminé</Badge>
                    )}
                  </div>
                  <h3 className="font-serif text-lg leading-tight mb-1"
                      style={{ color: "#0B2545" }}>{c.title}</h3>
                  <p className="text-[12px]" style={{ color: "#94A3B8" }}>
                    {c.description}
                  </p>
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between text-[11px] mb-1"
                       style={{ color: "#94A3B8" }}>
                    <span>{c.module_count} modules · {c.total_minutes} min · {c.ce_credits} UFC</span>
                    <span>{c.progress_pct}%</span>
                  </div>
                  <div className="h-1.5 rounded-full mb-3"
                       style={{ background: "#F1F5F9" }}>
                    <div className="h-full rounded-full"
                         style={{ width: `${c.progress_pct}%`,
                                    background: c.completed ? "#16A34A" : "#C5A880" }}
                         data-testid={`lms-course-progress-${c.key}`} />
                  </div>
                  <PrimaryButton onClick={() => setActiveKey(c.key)}
                                 testid={`lms-course-open-${c.key}`}>
                    <PlayCircle size={13} />
                    {c.progress_pct > 0 && c.progress_pct < 100 ? "Reprendre" : "Ouvrir"}
                  </PrimaryButton>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </PageShell>
  );
}


// ── Course detail (with modules + quizzes) ─────────────────────────

function CourseDetail({ courseKey, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModId, setActiveModId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/lms/courses/${courseKey}`);
      setData(data);
      if (!activeModId) setActiveModId(data.course.modules[0]?.id || null);
    } catch { toast.error("Chargement impossible"); }
    finally { setLoading(false); }
  }, [courseKey, activeModId]);

  useEffect(() => { load(); }, [courseKey]); // eslint-disable-line

  const activeModule = data?.course?.modules?.find(m => m.id === activeModId);
  const completedSet = new Set(data?.progress?.completed_modules || []);
  const quizScores   = data?.progress?.quiz_scores || {};

  const markComplete = async () => {
    if (!activeModule) return;
    try {
      await api.post(`/lms/courses/${courseKey}/modules/${activeModule.id}/complete`);
      toast.success("Module marqué comme lu");
      load();
    } catch { toast.error("Impossible d'enregistrer"); }
  };

  return (
    <PageShell testid="lms-detail-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <GhostButton onClick={onBack} testid="lms-detail-back">
          <ArrowLeft size={13} /> Retour au catalogue
        </GhostButton>

        {loading && <div className="py-16 flex justify-center"><Loader /></div>}

        {!loading && data && (
          <>
            <PageHeader
              eyebrow={data.course.category}
              title={data.course.title}
              subtitle={data.course.description}
              testid="lms-detail-header"
            />
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              {/* Modules sidebar */}
              <Card className="p-3" testid="lms-detail-modules-card">
                <ul className="space-y-1">
                  {data.course.modules.map((m) => {
                    const isActive = m.id === activeModId;
                    const isDone   = completedSet.has(m.id);
                    return (
                      <li key={m.id}>
                        <button
                          onClick={() => setActiveModId(m.id)}
                          data-testid={`lms-module-tab-${m.id}`}
                          className="w-full text-left rounded-md px-2 py-2 flex items-center gap-2 text-[12px] transition"
                          style={{
                            background: isActive ? "#0B2545" : "transparent",
                            color:      isActive ? "#fff" : "#0B2545",
                            fontWeight: isActive ? 600 : 500,
                          }}>
                          {isDone
                            ? <CheckCircle2 size={12} style={{ color: "#16A34A" }} />
                            : <span className="w-3 h-3 rounded-full inline-block"
                                    style={{ border: "2px solid #C5A88055" }} />}
                          <span className="flex-1 truncate">{m.title}</span>
                          <span className="text-[10px] opacity-70">{m.duration_minutes} min</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </Card>

              {/* Active module */}
              <div className="lg:col-span-3">
                {activeModule && (
                  <Card className="p-5" testid="lms-active-module-card">
                    <div className="prose prose-sm max-w-none"
                         data-testid="lms-module-content">
                      {activeModule.content_md.split("\n").map((line, i) => {
                        if (line.startsWith("# "))
                          return <h1 key={i} className="font-serif text-2xl"
                                     style={{ color: "#0B2545" }}>{line.slice(2)}</h1>;
                        if (line.startsWith("**") && line.endsWith("**"))
                          return <p key={i} className="font-semibold my-1"
                                    style={{ color: "#0B2545" }}>{line.slice(2, -2)}</p>;
                        if (line.startsWith("- "))
                          return <li key={i} className="text-[13px] ml-4"
                                     style={{ color: "#334155" }}>{line.slice(2)}</li>;
                        if (/^\d+\.\s/.test(line))
                          return <li key={i} className="text-[13px] ml-4"
                                     style={{ color: "#334155" }}>{line.replace(/^\d+\.\s/, "")}</li>;
                        if (line.trim() === "") return <div key={i} className="h-2" />;
                        return <p key={i} className="text-[13px]"
                                   style={{ color: "#334155" }}>{line}</p>;
                      })}
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                      <PrimaryButton onClick={markComplete}
                                     testid={`lms-mark-complete-${activeModule.id}`}>
                        <CheckCircle2 size={13} /> J'ai lu ce module
                      </PrimaryButton>
                      {completedSet.has(activeModule.id) && (
                        <Badge variant="green">Lu</Badge>
                      )}
                    </div>
                  </Card>
                )}

                {/* Quiz */}
                {activeModule?.quiz?.length > 0 && (
                  <div className="mt-4">
                    <QuizPanel
                      courseKey={courseKey}
                      moduleId={activeModule.id}
                      questions={activeModule.quiz}
                      previousScore={quizScores[activeModule.id]}
                      onSubmitted={load}
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </PageShell>
  );
}


function QuizPanel({ courseKey, moduleId, questions, previousScore, onSubmitted }) {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (Object.keys(answers).length < questions.length) {
      toast.error("Répondez à toutes les questions"); return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(
        `/lms/courses/${courseKey}/modules/${moduleId}/quiz`,
        { module_id: moduleId, answers },
      );
      setResult(data);
      if (data.passed) toast.success(`Quiz réussi · ${data.score_pct}%`);
      else            toast.warning(`Score ${data.score_pct}% — 70% requis pour valider`);
      onSubmitted?.();
    } catch { toast.error("Erreur"); }
    finally { setSubmitting(false); }
  };

  return (
    <Card className="p-5" testid="lms-quiz-card">
      <Section title="Quiz de validation"
               hint={`Seuil de réussite : 70 %${
                 previousScore ? ` · meilleur score précédent : ${previousScore}%` : ""
               }`}>
        {questions.map((q, qi) => (
          <div key={q.id} className="mb-4" data-testid={`lms-quiz-q-${q.id}`}>
            <div className="text-[13px] font-semibold mb-2"
                 style={{ color: "#0B2545" }}>
              {qi + 1}. {q.prompt}
            </div>
            <ul className="space-y-1">
              {q.choices.map(c => {
                const chosen = answers[q.id] === c.id;
                const shown  = result?.details?.find(d => d.question_id === q.id);
                let tone = "#334155";
                if (shown) {
                  if (c.id === shown.correct_choice_id) tone = "#16A34A";
                  else if (c.id === shown.user_choice_id) tone = "#B91C1C";
                }
                return (
                  <li key={c.id}>
                    <label className="flex items-center gap-2 cursor-pointer text-[12px]">
                      <input type="radio" name={q.id} value={c.id}
                             checked={chosen}
                             onChange={() => setAnswers({ ...answers, [q.id]: c.id })}
                             disabled={!!result}
                             data-testid={`lms-quiz-choice-${q.id}-${c.id}`}
                             style={{ accentColor: "#0B2545" }} />
                      <span style={{ color: tone,
                                       fontWeight: chosen ? 600 : 400 }}>{c.label}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
            {result && q.explanation && (
              <div className="mt-1 text-[11px] italic" style={{ color: "#94A3B8" }}>
                {q.explanation}
              </div>
            )}
          </div>
        ))}
        {!result ? (
          <PrimaryButton onClick={submit} disabled={submitting}
                         testid="lms-quiz-submit">
            {submitting ? "Envoi…" : "Valider mes réponses"}
          </PrimaryButton>
        ) : (
          <Alert variant={result.passed ? "success" : "warning"}
                 title={`Résultat : ${result.score_pct}% (${result.correct_count}/${result.total})`}
                 testid="lms-quiz-result">
            {result.passed
              ? "Bravo ! Ce module est validé."
              : "Relisez le cours et retentez le quiz."}
          </Alert>
        )}
      </Section>
    </Card>
  );
}
